/**
 * React Query hooks for Scoreboard (game results and leagues).
 * Uses placeholderData: keepPreviousData so filter/league changes don't clear the UI while fetching.
 * When range + timezone are provided (date view), query key uses week bounds for cache hits.
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { fetchGameResults, fetchLeaguesForResults } from '../helpers/gameResultsApi';
import type { LeagueOption } from '../helpers/gameResultsApi';
import type { WeekRange } from '../helpers/dateRange.helper';
import { FILTER_TYPES, DATE_SORT_TYPES } from '../constants/gameResults.constants';
import type { FilterType, DateSortType } from '../types/gameResults.types';

const STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes
const LEAGUES_STALE_MS = 5 * 60 * 1000; // 5 minutes

function getSortParam(filterType: FilterType, dateSortType: DateSortType): string {
  if (
    filterType === FILTER_TYPES.DATE ||
    filterType === FILTER_TYPES.DATE_DESC ||
    filterType === FILTER_TYPES.DATE_ASC
  ) {
    return dateSortType === DATE_SORT_TYPES.NEWEST ? FILTER_TYPES.DATE_DESC : FILTER_TYPES.DATE_ASC;
  }
  return filterType;
}

export function useLeaguesForResultsQuery() {
  return useQuery({
    queryKey: ['gameResults', 'leagues'],
    queryFn: fetchLeaguesForResults,
    staleTime: LEAGUES_STALE_MS,
  });
}

export function useGameResultsQuery(
  filterType: FilterType,
  selectedLeague: string,
  dateSortType: DateSortType,
  range?: WeekRange | null,
  timezone?: string | null,
  enabled = true
) {
  const sortParam = getSortParam(filterType, dateSortType);
  const useDateRange = Boolean(range?.start && range?.end && timezone);

  const dateRange = useDateRange && range
    ? { start: range.start, end: range.end }
    : undefined;

  const queryKey = ['gameResults', 'results', { sortParam, selectedLeague, dateRange }];

  const queryFn = () =>
    dateRange
      ? fetchGameResults(sortParam, selectedLeague, {
          start: dateRange.start,
          end: dateRange.end,
          timezone: timezone!,
        })
      : fetchGameResults(sortParam, selectedLeague);

  return useQuery({
    queryKey,
    queryFn,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
    refetchOnMount: false,
    enabled,
  });
}

export type { LeagueOption };
